import { devLog, devError } from '../utils/devLogger';
/**
 * Template Submission Tracker
 * Shows who has / has not submitted for a fillable template.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Alert, Badge, Spinner, Row, Col, Form } from 'react-bootstrap';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import templateSubmissionService from '../services/templateSubmissionService';

const TemplateSubmissionTracker = ({ template }) => {
  const [learners, setLearners] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cohortMap, setCohortMap] = useState({});

  const templateCohortIds = useMemo(() => {
    if (!template) return [];
    if (Array.isArray(template.cohortIds) && template.cohortIds.length) return template.cohortIds;
    if (template.cohortId) return [template.cohortId];
    return [];
  }, [template]);

  const loadLearners = async () => {
    if (!template) return;
    if (templateCohortIds.length === 0) {
      setLearners([]);
      return;
    }
    const learnerRecords = [];
    for (let i = 0; i < templateCohortIds.length; i += 10) {
      const chunk = templateCohortIds.slice(i, i + 10);
      const learnersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'learner'),
        where('cohortId', 'in', chunk)
      );
      const snapshot = await getDocs(learnersQuery);
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        learnerRecords.push({
          id: docSnap.id,
          name: data.displayName || data.name || data.email || 'N/A',
          email: data.email || '',
          cohortId: data.cohortId || ''
        });
      });
    }
    setLearners(learnerRecords);
  };

  const loadSubmissions = async () => {
    if (!template) return;
    const submissionsData = await templateSubmissionService.getTemplateSubmissions(template.id);
    setSubmissions(submissionsData);
  };

  useEffect(() => {
    const load = async () => {
      if (!template) return;
      try {
        setLoading(true);
        setError('');
        await Promise.all([loadLearners(), loadSubmissions()]);
      } catch (err) {
        devError('Error loading template tracking:', err);
        setError('Failed to load submission tracking.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [template?.id]);

  useEffect(() => {
    const loadCohorts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cohorts'));
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          map[docSnap.id] = data.name || data.cohortName || docSnap.id;
        });
        setCohortMap(map);
      } catch (err) {
        devError('Error loading cohorts:', err);
      }
    };

    loadCohorts();
  }, []);

  const submissionByLearner = useMemo(() => {
    const map = new Map();
    submissions.forEach((submission) => {
      if (!submission.learnerId) return;
      if (!map.has(submission.learnerId)) {
        map.set(submission.learnerId, submission);
      }
    });
    return map;
  }, [submissions]);

  const filteredLearners = useMemo(() => {
    if (filterStatus === 'all') return learners;
    if (filterStatus === 'submitted') {
      return learners.filter((learner) => submissionByLearner.has(learner.id));
    }
    if (filterStatus === 'missing') {
      return learners.filter((learner) => !submissionByLearner.has(learner.id));
    }
    return learners;
  }, [learners, submissionByLearner, filterStatus]);

  const submittedCount = useMemo(
    () => learners.filter((learner) => submissionByLearner.has(learner.id)).length,
    [learners, submissionByLearner]
  );

  if (!template) {
    return null;
  }

  if (templateCohortIds.length === 0) {
    return (
      <Alert variant="warning" className="mb-0">
        This template is not assigned to a cohort. Assign cohorts to track submissions.
      </Alert>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col md={8}>
            <h6 className="mb-0">Submission Tracking - {template.title}</h6>
          </Col>
          <Col md={4}>
            <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Learners</option>
              <option value="submitted">Submitted</option>
              <option value="missing">Not Submitted</option>
            </Form.Select>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" size="sm" />
            <p className="mt-2">Loading submission tracking...</p>
          </div>
        ) : (
          <>
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Badge bg="info">Total learners: {learners.length}</Badge>
              <Badge bg="success">Submitted: {submittedCount}</Badge>
              <Badge bg="danger">Not submitted: {Math.max(learners.length - submittedCount, 0)}</Badge>
            </div>
            {filteredLearners.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No learners found for this template.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Cohort</th>
                      <th>Status</th>
                      <th>Submitted At</th>
                      <th>Grade</th>
                      <th>Submitted By</th>
                      <th>File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLearners.map((learner) => {
                      const submission = submissionByLearner.get(learner.id);
                      const submittedAt = submission?.submittedAt?.toDate
                        ? submission.submittedAt.toDate()
                        : submission?.submittedAt
                          ? new Date(submission.submittedAt)
                          : null;
                      return (
                        <tr key={learner.id}>
                          <td>
                            <div><strong>{learner.name}</strong></div>
                            <small className="text-muted">{learner.email}</small>
                          </td>
                          <td>{cohortMap[learner.cohortId] || learner.cohortId || '-'}</td>
                          <td>
                            {submission ? (
                              <Badge bg={submission.status === 'approved' ? 'success' : submission.status === 'rejected' ? 'danger' : 'warning'}>
                                {submission.status || 'submitted'}
                              </Badge>
                            ) : (
                              <Badge bg="secondary">missing</Badge>
                            )}
                          </td>
                          <td>{submittedAt ? submittedAt.toLocaleString() : '-'}</td>
                          <td>{submission?.grade || '-'}</td>
                          <td>
                            {submission
                              ? submission.submittedOnBehalf
                                ? submission.submittedByName || 'Facilitator'
                                : 'Learner'
                              : '-'}
                          </td>
                          <td>
                            {submission?.fileURL ? (
                              <a href={submission.fileURL} target="_blank" rel="noreferrer">Download</a>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default TemplateSubmissionTracker;
